import { useEffect, useMemo, useRef, useState } from "react";

import { getReportPreview, listProducts, reportSendExternal, reportSendInternal } from "@/lib/api";
import type { MpInfo, ReportKind, ReportPreview } from "@/types/api";

// ──────────────────────────────────────────────────────── helpers
const pct3 = (v: number): string => (v * 100).toFixed(3);

function signClass(v: number): string {
  if (v > 0) return "text-up";
  if (v < 0) return "text-down";
  return "text-ink-mute";
}

type SendStatus = "idle" | "sending" | "ok" | "error";

interface SendState {
  status:        SendStatus;
  detail?:       string | null;
  promoted_rows?: number | null;
  promoted_date?: string | null;
}

// ──────────────────────────────────────────────────────── ReportPage
export function ReportPage() {
  const [products, setProducts] = useState<MpInfo[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listProducts()
      .then((d) => { if (!cancelled) setProducts(d); })
      .catch((e) => {
        if (cancelled) return;
        setListError(e?.response?.data?.detail ?? e?.message ?? "Failed to load products");
      });
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    if (!products) return null;
    const map = new Map<string, MpInfo[]>();
    for (const p of products) {
      const k = p.product_line ?? "—";
      const arr = map.get(k) ?? [];
      arr.push(p);
      map.set(k, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [products]);

  if (listError) {
    return (
      <div className="rounded-md bg-down/10 border border-down/30 px-3 py-2 text-sm text-down">
        {listError}
      </div>
    );
  }
  if (!grouped) return <p className="text-ink-mute">Loading products…</p>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ReportPane kind="internal" grouped={grouped} />
      <ReportPane kind="external" grouped={grouped} />
    </div>
  );
}

// ──────────────────────────────────────────────────────── pane
function ReportPane({
  kind, grouped,
}: {
  kind: ReportKind;
  grouped: [string, MpInfo[]][];
}) {
  const [selected, setSelectedState] = useState<Set<string>>(() => new Set());
  // Per-mp_code preview + load state, keyed by mp_code.
  const [previews, setPreviews] = useState<Map<string, ReportPreview>>(() => new Map());
  const [errors,   setErrors]   = useState<Map<string, string>>(() => new Map());
  const [loading,  setLoading]  = useState<Set<string>>(() => new Set());
  // Per-mp_code send state.
  const [sendStates, setSendStates] = useState<Map<string, SendState>>(() => new Map());
  const [sendingAll, setSendingAll] = useState(false);

  // We previously drove fetches from a useEffect on [selected], but that
  // cancels in-flight fetches every time selected mutates — picking two
  // codes in quick succession left the first stuck on "Loading…". Switch
  // to an imperative diff: start fetches for newly-added codes, clear
  // state for removed ones. Race-safe because each completion checks the
  // current selected via a ref.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const fetchPreview = (mp: string) => {
    setLoading((prev) => new Set(prev).add(mp));
    setErrors((prev) => { const n = new Map(prev); n.delete(mp); return n; });
    getReportPreview(mp, kind)
      .then((p) => {
        if (!selectedRef.current.has(mp)) return;
        setPreviews((prev) => new Map(prev).set(mp, p));
      })
      .catch((e: unknown) => {
        if (!selectedRef.current.has(mp)) return;
        const msg =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
          ?? (e as { message?: string })?.message
          ?? "Preview failed";
        setErrors((prev) => new Map(prev).set(mp, msg));
      })
      .finally(() => {
        setLoading((prev) => { const n = new Set(prev); n.delete(mp); return n; });
      });
  };

  const setSelected = (next: Set<string>) => {
    const prev = selectedRef.current;
    // Update the ref synchronously so back-to-back picker events compare
    // against intent, not against the stale rendered state.
    selectedRef.current = next;
    const added:   string[] = [];
    const removed: string[] = [];
    for (const mp of next) if (!prev.has(mp)) added.push(mp);
    for (const mp of prev) if (!next.has(mp)) removed.push(mp);

    if (removed.length) {
      setPreviews((m)   => { const n = new Map(m); for (const r of removed) n.delete(r); return n; });
      setErrors((m)     => { const n = new Map(m); for (const r of removed) n.delete(r); return n; });
      setSendStates((m) => { const n = new Map(m); for (const r of removed) n.delete(r); return n; });
      setLoading((m)    => { const n = new Set(m); for (const r of removed) n.delete(r); return n; });
    }
    setSelectedState(next);
    for (const mp of added) fetchPreview(mp);
  };

  const sortedSelected = useMemo(() => {
    return Array.from(selected).sort();
  }, [selected]);

  // Aggregate "can send": at least one selected with non-empty preview + recipients.
  const sendablePreviews = sortedSelected
    .map((mp) => previews.get(mp))
    .filter((p): p is ReportPreview => !!p && p.rows.length > 0 && p.recipients.length > 0);
  const canSend = !sendingAll && sendablePreviews.length > 0;

  const title = kind === "internal" ? "Internal report" : "External report";
  const subtitle = kind === "internal"
    ? "mp_weight (production) vs. mp_weight_check (proposed). Confirm promotes to mp_weight and emails the internal list."
    : "Previous vs. latest mp_weight snapshot. Confirm emails the external (client) list.";

  const onConfirm = async () => {
    if (!canSend) return;
    const note = `Send ${sendablePreviews.length} report${sendablePreviews.length === 1 ? "" : "s"}? ` +
      sendablePreviews.map((p) => `${p.mp_code} → ${p.recipients.length}`).join(", ");
    if (!window.confirm(note)) return;
    setSendingAll(true);
    // Sequential sends so we don't blast SMTP and so the operator can see
    // per-row progress in the UI.
    for (const p of sendablePreviews) {
      setSendStates((prev) => new Map(prev).set(p.mp_code, { status: "sending" }));
      try {
        const result = kind === "internal"
          ? await reportSendInternal(p.mp_code)
          : await reportSendExternal(p.mp_code);
        if (result.status === "OK") {
          setSendStates((prev) => new Map(prev).set(p.mp_code, {
            status: "ok",
            promoted_rows: result.promoted_rows,
            promoted_date: result.promoted_date,
          }));
          // Refresh the preview so the operator sees the post-state (for
          // internal, the change column collapses to 0 after promote).
          try {
            const updated = await getReportPreview(p.mp_code, kind);
            setPreviews((prev) => new Map(prev).set(p.mp_code, updated));
          } catch { /* preview refresh failures are non-fatal */ }
        } else {
          setSendStates((prev) => new Map(prev).set(p.mp_code, {
            status: "error",
            detail: result.detail ?? "Send failed",
          }));
        }
      } catch (e: unknown) {
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
          ?? (e as { message?: string })?.message
          ?? "Send failed";
        setSendStates((prev) => new Map(prev).set(p.mp_code, { status: "error", detail }));
      }
    }
    setSendingAll(false);
  };

  return (
    <section className="rounded-md bg-panel border border-line/60 flex flex-col min-h-[600px]">
      {/* ───── header */}
      <div className="border-b border-line/60 px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <p className="text-xs text-ink-dim mt-0.5">{subtitle}</p>
        </div>
        <MpCodeMultiPicker
          grouped={grouped}
          selected={selected}
          onChange={setSelected}
        />
      </div>

      {/* ───── body */}
      <div className="flex-1 px-4 py-4 space-y-5 overflow-x-auto">
        {sortedSelected.length === 0 && (
          <p className="text-sm text-ink-dim">Pick one or more mp_codes from the dropdown.</p>
        )}
        {sortedSelected.map((mp) => {
          const p = previews.get(mp);
          const err = errors.get(mp);
          const isLoading = loading.has(mp);
          const send = sendStates.get(mp);
          return (
            <PreviewBlock
              key={mp}
              mp_code={mp}
              preview={p}
              error={err}
              loading={isLoading}
              send={send}
            />
          );
        })}
      </div>

      {/* ───── footer */}
      <div className="border-t border-line/60 px-4 py-3">
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canSend}
          className={
            "w-full rounded-md py-2 font-semibold transition-colors " +
            (canSend
              ? "bg-accent text-white hover:bg-accent/90"
              : "bg-panel-2 text-ink-dim cursor-not-allowed")
          }
        >
          {sendingAll
            ? `Sending… (${
                Array.from(sendStates.values()).filter((s) => s.status === "ok").length
              }/${sendablePreviews.length})`
            : sendablePreviews.length > 0
              ? `Confirm and Send (${sendablePreviews.length})`
              : "Confirm and Send"}
        </button>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────── PreviewBlock
function PreviewBlock({
  mp_code, preview, error, loading, send,
}: {
  mp_code: string;
  preview: ReportPreview | undefined;
  error: string | undefined;
  loading: boolean;
  send: SendState | undefined;
}) {
  return (
    <div className="rounded-md bg-panel-2/60 border border-line/40 px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-baseline gap-3">
          <span className="text-xs uppercase tracking-wider text-ink-dim">mp_code</span>
          <span className="font-mono text-ink">{mp_code}</span>
        </div>
        {send && <SendBadge state={send} />}
      </div>

      {loading && <p className="text-sm text-ink-dim">Loading preview…</p>}
      {error && (
        <div className="rounded-md bg-down/10 border border-down/30 px-3 py-2 text-sm text-down">
          {error}
        </div>
      )}
      {preview && !loading && !error && <PreviewTable preview={preview} />}
    </div>
  );
}

function SendBadge({ state }: { state: SendState }) {
  if (state.status === "idle") return null;
  const tone =
    state.status === "ok"      ? "bg-up/15 border-up/30 text-up" :
    state.status === "error"   ? "bg-down/15 border-down/30 text-down" :
    /* sending */                "bg-accent/15 border-accent/30 text-accent";
  let label: string;
  if (state.status === "sending") {
    label = "sending…";
  } else if (state.status === "ok") {
    label = state.promoted_rows != null
      ? `sent · promoted ${state.promoted_rows} rows`
      : "sent";
  } else {
    label = state.detail ?? "send failed";
  }
  return (
    <span className={"text-xs rounded px-2 py-0.5 border " + tone}>{label}</span>
  );
}

function PreviewTable({ preview }: { preview: ReportPreview }) {
  const hasRows = preview.rows.length > 0;
  const changeIsMeaningful = preview.kind === "internal"
    ? preview.old_date && preview.new_date
    : preview.old_date != null;

  return (
    <div className="space-y-2">
      {!hasRows ? (
        <div className="rounded-md bg-panel border border-line/40 px-3 py-2 text-sm text-ink-dim">
          No snapshot rows. {preview.kind === "internal"
            ? "Need data in mp_weight_check (or mp_weight)."
            : "Need at least one mp_weight snapshot."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-ink-dim text-xs uppercase tracking-wider">
                <th className="text-left  py-1 pr-3"></th>
                <th className="text-right py-1 px-2">{preview.old_label}</th>
                <th className="text-right py-1 px-2">{preview.new_label}</th>
                <th className="text-right py-1 pl-2">change</th>
              </tr>
              <tr className="text-ink-dim border-b border-line/40">
                <th className="text-left  py-1 pr-3 font-normal">date</th>
                <th className="text-right py-1 px-2 font-mono font-normal">
                  {preview.old_date ?? "—"}
                </th>
                <th className="text-right py-1 px-2 font-mono font-normal">
                  {preview.new_date ?? "—"}
                </th>
                <th className="text-right py-1 pl-2 text-ink-dim font-normal">—</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.stock_code} className="border-b border-line/20">
                  <td className="py-1 pr-3 font-mono text-ink">{r.stock_code}</td>
                  <td className="py-1 px-2 text-right font-mono text-ink-mute">
                    {pct3(r.weight_old)}
                  </td>
                  <td className="py-1 px-2 text-right font-mono text-ink">
                    {pct3(r.weight_new)}
                  </td>
                  <td className={"py-1 pl-2 text-right font-mono " + (changeIsMeaningful ? signClass(r.change) : "text-ink-dim")}>
                    {changeIsMeaningful
                      ? `${r.change > 0 ? "+" : ""}${pct3(r.change)}`
                      : "—"}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-line/60">
                <td className="py-1 pr-3 text-ink-dim uppercase text-xs">total</td>
                <td className="py-1 px-2 text-right font-mono text-ink">{pct3(preview.total_old)}</td>
                <td className="py-1 px-2 text-right font-mono text-ink">{pct3(preview.total_new)}</td>
                <td className={"py-1 pl-2 text-right font-mono " + (changeIsMeaningful ? signClass(preview.total_change) : "text-ink-dim")}>
                  {changeIsMeaningful
                    ? `${preview.total_change > 0 ? "+" : ""}${pct3(preview.total_change)}`
                    : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <RecipientList recipients={preview.recipients} kind={preview.kind} />
    </div>
  );
}

function RecipientList({ recipients, kind }: { recipients: string[]; kind: ReportKind }) {
  if (recipients.length === 0) {
    return (
      <div className="rounded-md bg-down/10 border border-down/30 px-3 py-2 text-xs text-down">
        No recipients in {kind === "internal" ? "mp_email_internal" : "mp_email_external"}.
      </div>
    );
  }
  return (
    <details className="text-xs text-ink-mute">
      <summary className="cursor-pointer hover:text-ink">
        Recipients ({recipients.length})
      </summary>
      <ul className="mt-1 pl-4 list-disc space-y-0.5">
        {recipients.map((r) => <li key={r} className="font-mono">{r}</li>)}
      </ul>
    </details>
  );
}

// ──────────────────────────────────────────────────────── MpCodeMultiPicker
// Same grouped-by-product_line UX as Portfolio's filter, but with "selected"
// (inclusion) semantics: nothing checked initially, user adds what they want.
// Per-product_line tri-state checkbox toggles the whole group.
function MpCodeMultiPicker({
  grouped, selected, onChange,
}: {
  grouped: [string, MpInfo[]][];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const totalProducts = grouped.reduce((n, [, items]) => n + items.length, 0);

  const toggle = (mp: string) => {
    const next = new Set(selected);
    if (next.has(mp)) next.delete(mp); else next.add(mp);
    onChange(next);
  };
  const setGroup = (codes: string[], include: boolean) => {
    const next = new Set(selected);
    for (const c of codes) {
      if (include) next.add(c); else next.delete(c);
    }
    onChange(next);
  };
  const selectAll = () => onChange(new Set(grouped.flatMap(([, items]) => items.map((p) => p.mp_code))));
  const clearAll  = () => onChange(new Set());

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          "flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm " +
          (open
            ? "bg-panel-2 border-accent text-ink"
            : "bg-panel border-line text-ink-mute hover:text-ink hover:border-ink-mute")
        }
        aria-expanded={open}
      >
        <span className="text-xs uppercase tracking-wider text-ink-dim">mp_code</span>
        <span className="text-ink">
          {selected.size}<span className="text-ink-dim">/{totalProducts}</span>
        </span>
        <span className={"text-ink-dim transition-transform " + (open ? "rotate-180" : "")}>▾</span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 z-20 rounded-md bg-panel border border-line/60 shadow-xl p-3"
          role="dialog"
        >
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={selectAll}
              className="flex-1 text-xs px-2 py-1 rounded border border-line text-ink-mute hover:text-ink hover:border-ink-mute"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="flex-1 text-xs px-2 py-1 rounded border border-line text-ink-mute hover:text-ink hover:border-ink-mute"
            >
              Clear all
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1 space-y-3">
            {grouped.map(([line, items]) => {
              const codes = items.map((p) => p.mp_code);
              const includedCount = codes.filter((c) => selected.has(c)).length;
              const groupState: "all" | "none" | "some" =
                includedCount === 0 ? "none" :
                includedCount === codes.length ? "all" :
                "some";
              return (
                <div key={line}>
                  <label className="flex items-center gap-2 cursor-pointer select-none py-1">
                    <TriCheckbox
                      state={groupState}
                      onChange={() => setGroup(codes, groupState !== "all")}
                    />
                    <span className="text-sm font-medium text-ink capitalize">{line}</span>
                  </label>
                  <ul className="pl-6 space-y-0.5 mt-0.5">
                    {items.map((p) => (
                      <li key={p.mp_code}>
                        <label className="flex items-center gap-2 cursor-pointer select-none py-0.5">
                          <input
                            type="checkbox"
                            checked={selected.has(p.mp_code)}
                            onChange={() => toggle(p.mp_code)}
                            className="h-3.5 w-3.5 accent-accent cursor-pointer"
                          />
                          <span className="font-mono text-xs text-ink-mute hover:text-ink">
                            {p.mp_code}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TriCheckbox({
  state, onChange,
}: { state: "all" | "none" | "some"; onChange: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === "all"}
      onChange={onChange}
      className="h-4 w-4 accent-accent cursor-pointer"
    />
  );
}
